import { Text } from "@clipboard-health/ui-components";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import pluralize from "pluralize";

import { Button } from "../../components/Button";
import { useVirtualShiftListContext } from "../../Shift/Open/useVirtualShiftListContext/useVirtualShiftListContext";
import { useShiftDiscoveryUserFiltersContext } from "./useUserFiltersContext";

interface ClearFiltersButtonProps {
  allOpenShiftsCount: number;
}

export function ClearFiltersButton(props: ClearFiltersButtonProps) {
  const { allOpenShiftsCount } = props;
  const {
    clearFilters,
    appliedFiltersCount,
    dates,
    clearDates,
    shiftTimeSlots,
    setShiftTimeSlots,
  } = useShiftDiscoveryUserFiltersContext();
  const { visibleOpenShiftsCount, listRef } = useVirtualShiftListContext();

  const hiddenOpenShiftsCount = allOpenShiftsCount - visibleOpenShiftsCount;

  const isFiltersRedesignEnabled = useCbhFlag(CbhFeatureFlag.SHIFT_DISCOVERY_FILTERS_REDESIGN, {
    defaultValue: false,
  });

  if (isFiltersRedesignEnabled && dates.length === 0 && shiftTimeSlots.length === 0) {
    return null;
  }

  if (!isFiltersRedesignEnabled && appliedFiltersCount === 0 && dates.length === 0) {
    return null;
  }

  return (
    <Button
      size="medium"
      variant="outlined"
      sx={{ gap: 1 }}
      onClick={() => {
        logEvent(APP_V2_USER_EVENTS.OPEN_SHIFTS_FILTERS_CLEARED, {
          trigger: "List View",
        });

        if (isFiltersRedesignEnabled) {
          clearDates();
          setShiftTimeSlots([]);
        } else {
          clearFilters();
        }

        listRef.current?.scrollToIndex(0);
      }}
    >
      <Text semibold variant="body2">
        {isFiltersRedesignEnabled
          ? "Show all times"
          : `Clear ${pluralize("filter", appliedFiltersCount)}`}
      </Text>
      {!isFiltersRedesignEnabled && hiddenOpenShiftsCount > 0 && (
        <Text semibold variant="caption" color={(theme) => theme.palette.text.tertiary}>
          +{hiddenOpenShiftsCount} {pluralize("shift", hiddenOpenShiftsCount)}
        </Text>
      )}
    </Button>
  );
}
